/**
 * ---------------------------------------------------------------------------------------------------------
 * <infinite-scroll> — custom pagination for product grids (Stretch 2.1.0 / MASKA)
 * ---------------------------------------------------------------------------------------------------------
 *
 * Progressive enhancement on top of the theme's existing {% paginate %} markup:
 *
 *   - With JS disabled (or before the element upgrades), the inner <a rel="next"> is a plain link to page 2.
 *     Crawlers and no-JS users get normal, working pagination.
 *   - Once upgraded, the element fetches the next page through the Section Rendering API
 *     (?page=N&section_id=X), pulls the <product-list> children out of the response and appends them
 *     to the existing grid.
 *
 * Modes (set via the `mode` attribute):
 *   - "load_more" : user clicks a button for each additional page
 *   - "infinite"  : pages load automatically as a sentinel enters the viewport, up to `max-auto-loads`
 *                   pages, after which the button reappears so the footer stays reachable.
 *
 * This file is intentionally standalone — it does not import from `theme`, so a theme update that
 * rewrites theme.js cannot break it.
 * ---------------------------------------------------------------------------------------------------------
 */

(() => {
  if (window.customElements.get('infinite-scroll')) return;

  const SUPPORTS_OBSERVER = 'IntersectionObserver' in window;

  /**
   * On reload, the browser restores the scroll position from a document that may have contained
   * several loaded batches. The fresh document only has the first batch, so that position lands the
   * customer at the very bottom of a much shorter page. Opt out of restoration for reloads only.
   *
   * Back/forward navigation is deliberately left alone: there the restored position is still
   * correct, and the back/forward cache usually returns the loaded batches along with it.
   */
  const suppressStaleScrollRestoration = () => {
    if (!('scrollRestoration' in history)) return;
    if (!document.querySelector('infinite-scroll')) return;

    const navigation = performance.getEntriesByType?.('navigation')?.[0];

    if (navigation?.type === 'reload') {
      history.scrollRestoration = 'manual';
    }
  };

  suppressStaleScrollRestoration();

  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      // Guard against re-initialisation if the element is moved rather than re-created.
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.isLoading = false;
      this.autoLoadsUsed = 0;

      this.button = this.querySelector('[data-infinite-scroll-button]');
      this.status = this.querySelector('[data-infinite-scroll-status]');
      this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');

      this.button?.addEventListener('click', this.onButtonClick.bind(this), {
        signal: this.abortController.signal,
      });

      // In "infinite" mode the button is only a fallback, so hide it while auto-loading is available.
      // Hiding happens in JS (never in CSS) so the link stays visible when scripting is unavailable.
      if (this.usesAutoLoad) {
        this.button?.setAttribute('hidden', '');
      }

      this.setupObserver();
      this.updateStatus();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
    }

    /* --------------------------------------------------------------------- */
    /* Attribute accessors                                                    */
    /* --------------------------------------------------------------------- */

    get mode() {
      return this.getAttribute('mode') || 'load_more';
    }

    get usesAutoLoad() {
      return this.mode === 'infinite' && SUPPORTS_OBSERVER;
    }

    /** Number of pages loaded automatically before falling back to the button. 0 = unlimited. */
    get maxAutoLoads() {
      const value = parseInt(this.getAttribute('max-auto-loads'), 10);
      return Number.isNaN(value) ? 0 : value;
    }

    get autoLoadBudgetLeft() {
      return this.maxAutoLoads === 0 || this.autoLoadsUsed < this.maxAutoLoads;
    }

    get nextUrl() {
      return this.getAttribute('next-url');
    }

    get sectionId() {
      return this.getAttribute('section-id');
    }

    get productList() {
      const id = this.getAttribute('target');
      return id ? document.getElementById(id) : null;
    }

    get shouldUpdateUrl() {
      return this.hasAttribute('update-url');
    }

    /* --------------------------------------------------------------------- */
    /* Auto-loading                                                           */
    /* --------------------------------------------------------------------- */

    setupObserver() {
      this.observer?.disconnect();

      if (!this.usesAutoLoad || !this.sentinel || !this.nextUrl || !this.autoLoadBudgetLeft) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage(true);
          }
        },
        { rootMargin: '800px 0px' }
      );

      this.observer.observe(this.sentinel);
    }

    /** Auto-loading budget is spent: disconnect the observer and reveal the button. */
    exhaustAutoLoad() {
      this.observer?.disconnect();
      this.button?.removeAttribute('hidden');
    }

    onButtonClick(event) {
      event.preventDefault();

      // A deliberate click is a signal that the customer wants to keep going, so in infinite mode
      // we grant a fresh auto-load budget and hide the button again.
      if (this.usesAutoLoad) {
        this.autoLoadsUsed = 0;
        this.button?.setAttribute('hidden', '');
      }

      this.loadNextPage(false);
    }

    /* --------------------------------------------------------------------- */
    /* Loading                                                                */
    /* --------------------------------------------------------------------- */

    async loadNextPage(isAutoLoad = false) {
      const productList = this.productList;

      if (this.isLoading || !this.nextUrl || !productList) return;

      this.isLoading = true;
      this.setBusy(true);

      try {
        const historyUrl = new URL(this.nextUrl, window.location.origin);
        const fetchUrl = new URL(historyUrl);
        fetchUrl.searchParams.set('section_id', this.sectionId);

        const response = await fetch(fetchUrl.toString(), { signal: this.abortController.signal });

        if (!response.ok) throw new Error(`Unexpected status ${response.status}`);

        const document_ = new DOMParser().parseFromString(await response.text(), 'text/html');
        const newProductList = document_.querySelector('product-list');
        const newPagination = document_.querySelector('infinite-scroll');

        const firstAddedCard = this.appendProducts(productList, newProductList);

        if (isAutoLoad) {
          this.autoLoadsUsed += 1;
        }

        // Advance (or terminate) the pagination state based on what the next page reported.
        const followingUrl = newPagination?.getAttribute('next-url');

        if (followingUrl) {
          this.setAttribute('next-url', followingUrl);
          if (this.button) this.button.href = followingUrl;
        } else {
          this.removeAttribute('next-url');
          this.setAttribute('data-complete', '');
          this.observer?.disconnect();
          this.button?.setAttribute('hidden', '');
        }

        if (this.shouldUpdateUrl) {
          history.replaceState({}, '', historyUrl.toString());
        }

        this.updateStatus();

        if (!this.autoLoadBudgetLeft && this.nextUrl) {
          this.exhaustAutoLoad();
        } else {
          this.setupObserver();
        }

        // Manual loads move focus to the first new card so keyboard and screen reader users
        // are not dropped back at the top of the grid.
        if (!isAutoLoad && firstAddedCard) {
          firstAddedCard.setAttribute('tabindex', '-1');
          firstAddedCard.focus({ preventScroll: true });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[infinite-scroll] Could not load the next page.', error);
          // Fall back to a real page load rather than leaving the customer stuck.
          this.button?.removeAttribute('hidden');
          this.observer?.disconnect();
        }
      } finally {
        this.isLoading = false;
        this.setBusy(false);
      }
    }

    /**
     * Moves the children of the fetched <product-list> into the live one.
     * Returns the first appended element so focus can be moved to it.
     */
    appendProducts(productList, newProductList) {
      if (!newProductList) return null;

      const fragment = document.createDocumentFragment();
      let firstAdded = null;

      Array.from(newProductList.children).forEach((child) => {
        const node = document.importNode(child, true);

        // The theme hides pre-reveal cards with `[reveal-on-scroll="true"] { opacity: 0 }` and only
        // animates them from <product-list>'s connectedCallback, which has already run. Strip the
        // attribute and run our own fade instead, otherwise appended cards stay invisible.
        node.removeAttribute('reveal-on-scroll');
        node.querySelectorAll('[reveal-on-scroll]').forEach((element) => element.removeAttribute('reveal-on-scroll'));
        node.classList.add('is-appended');

        if (!firstAdded) firstAdded = node;
        fragment.appendChild(node);
      });

      productList.appendChild(fragment);

      return firstAdded;
    }

    /* --------------------------------------------------------------------- */
    /* UI state                                                               */
    /* --------------------------------------------------------------------- */

    setBusy(isBusy) {
      this.toggleAttribute('data-loading', isBusy);
      // The theme's button styles render a loader whenever aria-busy is set.
      this.button?.setAttribute('aria-busy', isBusy ? 'true' : 'false');

      document.documentElement.dispatchEvent(
        new CustomEvent(isBusy ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
      );
    }

    updateStatus() {
      if (!this.status) return;

      const template = this.getAttribute('status-template');
      const total = this.getAttribute('total');
      const shown = this.productList?.querySelectorAll('product-card').length;

      if (!template || !total || shown === undefined) return;

      this.status.textContent = template.replace('__COUNT__', shown).replace('__TOTAL__', total);
    }
  }

  window.customElements.define('infinite-scroll', InfiniteScroll);
})();
